import { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Ruler, ChevronDown, Info, TrendingUp, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTodaySteps } from '@/hooks/useSteps';
import { useTodayActivity } from '@/hooks/useActivity';
import { useDayMeals } from '@/hooks/useMeals';
import { Loader } from '@/components/Loader';
import { ConsistencyGrid } from '@/components/ConsistencyGrid';
import { CourseDoneCard } from '@/components/CourseDoneCard';
import { StatsSlavaLine } from '@/components/StatsSlavaLine';
import { BodyChart } from '@/components/BodyChart';
import { getEffectiveBMR } from '@/lib/tdee';
import { Emoji } from '@/components/Emoji';
import { Mascot } from '@/components/Mascot';
type Range = 7 | 30 | 90;
interface DayPoint {
  date: string;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
}
interface MeasurementPoint {
  date: string;
  rawDate: string;
  bf: number | null;
  waist: number | null;
  neck: number | null;
  hips: number | null;
  glutes: number | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// КАРТОЧКА ЭНЕРГОБАЛАНСА — главное новое
// ═══════════════════════════════════════════════════════════════════════════
type BalanceType = 'good_deficit' | 'too_much' | 'balance' | 'surplus';

interface BalanceInsight {
  type: BalanceType;
  emoji: string;
  title: string;
  subtitle: string;
  detailIntro: string;
  detailPoints: string[];
  detailOutro: string;
  bgClass: string;
  borderClass: string;
  textClass: string;
}

function getBalanceInsight(
  consumed: number,
  burned: number,
  targetCalories: number
): BalanceInsight {
  const netCalories = consumed - burned;
  const deficitAbs = Math.abs(netCalories);
  const deficitPercent = (deficitAbs / targetCalories) * 100;

  // Профицит
  if (netCalories > 0) {
    return {
      type: 'surplus',
      emoji: '🍽',
      title: 'Сегодня день побольше',
      subtitle: `+${Math.round(netCalories)} ккал сверх нормы`,
      detailIntro: 'Это часть пути, а не повод корить себя.',
      detailPoints: [
        'Один день не делает результат',
        'Тело умеет балансировать на дистанции',
        'Главное — что покажет неделя в целом',
      ],
      detailOutro: 'Завтра новый старт. Без вины — просто продолжай.',
      bgClass: 'bg-sky-glow/5',
      borderClass: 'border-sky-glow/30',
      textClass: 'text-sky-glow',
    };
  }

  // Баланс (дефицит <10%)
  if (deficitPercent < 10) {
    return {
      type: 'balance',
      emoji: '⚖️',
      title: 'Сегодня в балансе',
      subtitle: 'Что съел — то и потратил, примерно равно',
      detailIntro: 'Дни баланса — нормальная фаза. Не каждый день нужен дефицит.',
      detailPoints: [
        'Вес держится',
        'Тело отдыхает',
        'Силы восстанавливаются',
      ],
      detailOutro: 'Завтра увидим как пойдёт.',
      bgClass: 'bg-bg-elevated/50',
      borderClass: 'border-line',
      textClass: 'text-ink-muted',
    };
  }

  // Комфортный дефицит (10-20%)
  if (deficitPercent <= 20) {
    return {
      type: 'good_deficit',
      emoji: '💚',
      title: 'Хороший темп',
      subtitle: `Сегодня минус ${Math.round(deficitAbs)} ккал — устойчивая скорость`,
      detailIntro: 'Это та самая «золотая середина», когда тело отдаёт лишнее, но не паникует.',
      detailPoints: [
        'Худеешь без срывов',
        'Силы и настроение в норме',
        'Мышцы остаются с тобой',
      ],
      detailOutro: 'Продолжай в том же ритме — результат придёт сам.',
      bgClass: 'bg-emerald-glow/5',
      borderClass: 'border-emerald-glow/30',
      textClass: 'text-emerald-glow',
    };
  }

  // Большой дефицит (>20%)
  return {
    type: 'too_much',
    emoji: '🔥',
    title: 'Сегодня темп бодрый',
    subtitle: `Минус ${Math.round(deficitAbs)} ккал — это серьёзная разница`,
    detailIntro: 'Один такой день — это нормально, особенно после праздников или активной тренировки.',
    detailPoints: [
      'Метаболизм замедляется',
      'Энергии становится меньше',
      'Срывы случаются чаще',
    ],
    detailOutro: 'Но если так каждый день — тело начинает «прижимать тормоза». Комфортная зона — минус 10-20% от нормы. Тело такое любит 💚',
    bgClass: 'bg-warn/5',
    borderClass: 'border-warn/30',
    textClass: 'text-warn',
  };
}

function EnergyBalanceCard() {
  const { profile } = useUserStore();
  const { totals } = useDayMeals();
  const { caloriesBurned: stepsBurned } = useTodaySteps();
  const { totalCalories: activityBurned } = useTodayActivity();
  const [expanded, setExpanded] = useState(false);

  if (!profile) return null;

  // Вычисляем BMR
  let bmr = 0;
  if (profile.sex && profile.weight_kg && profile.height_cm && profile.birth_date) {
    const result = getEffectiveBMR({
      sex: profile.sex,
      birth_date: profile.birth_date,
      height_cm: profile.height_cm,
      weight_kg: profile.weight_kg,
      custom_bmr: profile.custom_bmr,
      use_custom_bmr: profile.use_custom_bmr,
    });
    bmr = result.bmr;
  }

  // Если BMR нет — карточку не показываем
  if (bmr === 0) return null;

  const totalBurned = bmr + stepsBurned + activityBurned;
  const targetCalories = profile.daily_calories ?? 2000;
  const insight = getBalanceInsight(totals.calories, totalBurned, targetCalories);

  // Если юзер не ел сегодня — карточку не показываем
  if (totals.calories === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`card ${insight.bgClass} ${insight.borderClass} p-4 mb-5`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left active:scale-[0.99] transition-transform"
      >
        <div className="flex items-start gap-3">
          <span className="text-3xl shrink-0">{insight.emoji}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className={`font-display font-bold text-base ${insight.textClass}`}>
                {insight.title}
              </span>
            </div>
            <div className="text-xs text-ink-muted leading-snug">
              {insight.subtitle}
            </div>
          </div>
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="text-ink-dim shrink-0 mt-1"
          >
            <ChevronDown size={16} />
          </motion.div>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-3 mt-3 border-t border-line/40 text-xs leading-relaxed space-y-2">
              <p className="text-ink-muted">{insight.detailIntro}</p>
              <ul className="space-y-1 pl-1">
                {insight.detailPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-ink-muted">
                    <span className={insight.textClass}>—</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <p className="text-ink-muted pt-1">{insight.detailOutro}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// МЕНТАЛЬНАЯ ПЕРЕЗАГРУЗКА — «жидкая» шкала практик за неделю + пояснение (i)
// ═══════════════════════════════════════════════════════════════════════════
function MentalResetBar({ weekCount }: { weekCount: number }) {
  const [info, setInfo] = useState(false);
  const goal = 7;
  const pct = Math.min(100, Math.round((weekCount / goal) * 100));
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-ink inline-flex items-center gap-1">
          <Emoji e="🧠" className="w-4 h-4" /> Ментальная перезагрузка
          <button
            type="button"
            onClick={() => setInfo((v) => !v)}
            className="text-ink-dim hover:text-ink transition-colors"
            aria-label="Что это"
          >
            <Info size={13} />
          </button>
        </span>
        <span className="text-[11px] text-ink-dim">{weekCount}/{goal} практик за неделю</span>
      </div>

      <AnimatePresence>
        {info && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mb-2 rounded-xl bg-bg-elevated border border-line p-3 text-[11px] text-ink-muted leading-relaxed">
              Сколько практик для ума ты сделал за последние 7 дней. Цель — 7 (примерно одна в день). Засчитываются:
              <span className="block mt-1.5 space-y-0.5">
                <span className="block">🧘 медитации</span>
                <span className="block">🫁 дыхательные упражнения</span>
                <span className="block">🧭 шаги курса «Путь со Славой»</span>
              </span>
              <span className="block mt-1.5 text-ink-dim">Регулярность важнее длительности — даже пара минут в день перезагружают нервную систему.</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative h-9 rounded-2xl overflow-hidden bg-bg-elevated border border-line">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="absolute inset-y-0 left-0"
          style={{ background: 'linear-gradient(90deg, #10E58E, #3DD8FF)' }}
        >
          <motion.div
            className="absolute inset-y-0"
            style={{ width: '35%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.55), transparent)' }}
            animate={{ left: ['-35%', '135%'] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>
        <div className="absolute inset-0 flex items-center justify-end px-3">
          <span className="text-xs font-bold text-ink">{pct}%</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ОСНОВНОЙ ЭКРАН СТАТИСТИКИ
// ═══════════════════════════════════════════════════════════════════════════
export default function Stats() {
  const { profile } = useUserStore();
  const [range, setRange] = useState<Range>(7);
  const [data, setData] = useState<DayPoint[]>([]);
  const [weight, setWeight] = useState<{ date: string; w: number; raw: string }[]>([]);
  const [measurements, setMeasurements] = useState<MeasurementPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [practice, setPractice] = useState({ med: 0, breath: 0, courseDays: 0, weekCount: 0 });
  useEffect(() => {
    if (!profile) return;
    load();
  }, [range, profile?.id]);
  // Сводка практики (за всё время) + активность за неделю для шкалы перезагрузки
  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      const uid = profile.id;
      const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString();
      const [medRes, breathRes, courseRes, medWk, brWk, stepWk] = await Promise.all([
        supabase.from('meditation_history').select('id', { count: 'exact', head: true }).eq('user_id', uid).eq('completed', true),
        supabase.from('breathing_sessions').select('id', { count: 'exact', head: true }).eq('user_id', uid).eq('completed', true),
        supabase.from('user_course_progress').select('completed_days').eq('user_id', uid).maybeSingle(),
        supabase.from('meditation_history').select('id', { count: 'exact', head: true }).eq('user_id', uid).eq('completed', true).gte('created_at', weekAgo),
        supabase.from('breathing_sessions').select('id', { count: 'exact', head: true }).eq('user_id', uid).eq('completed', true).gte('created_at', weekAgo),
        supabase.from('user_course_step_log').select('id', { count: 'exact', head: true }).eq('user_id', uid).gte('completed_at', weekAgo),
      ]);
      setPractice({
        med: medRes.count ?? 0,
        breath: breathRes.count ?? 0,
        courseDays: (courseRes.data?.completed_days ?? []).length,
        weekCount: (medWk.count ?? 0) + (brWk.count ?? 0) + (stepWk.count ?? 0),
      });
    })();
  }, [profile?.id]);
  async function load() {
    setLoading(true);
    const since = subDays(startOfDay(new Date()), range - 1).toISOString();
    const sinceDate = format(subDays(new Date(), range - 1), 'yyyy-MM-dd');
    const [mealsRes, weightRes, measurementsRes] = await Promise.all([
      supabase
        .from('meals')
        .select('consumed_at,total_calories,total_protein_g,total_fat_g,total_carbs_g')
        .gte('consumed_at', since),
      supabase
        .from('weight_log')
        .select('logged_at,weight_kg')
        .gte('logged_at', since)
        .order('logged_at', { ascending: true }),
      supabase
        .from('body_measurements')
        .select('measured_at,neck_cm,waist_cm,hips_cm,glutes_cm,body_fat_percent')
        .gte('measured_at', sinceDate)
        .order('measured_at', { ascending: true }),
    ]);
    // Группируем еду по дням
    const map = new Map<string, DayPoint>();
    for (let i = 0; i < range; i++) {
      const d = format(subDays(new Date(), range - 1 - i), 'yyyy-MM-dd');
      map.set(d, { date: d, calories: 0, protein: 0, fat: 0, carbs: 0 });
    }
    (mealsRes.data ?? []).forEach((m: any) => {
      const d = format(new Date(m.consumed_at), 'yyyy-MM-dd');
      const p = map.get(d);
      if (p) {
        p.calories += m.total_calories;
        p.protein += Number(m.total_protein_g);
        p.fat += Number(m.total_fat_g);
        p.carbs += Number(m.total_carbs_g);
      }
    });
    setData(Array.from(map.values()));
    setWeight(
      (weightRes.data ?? []).map((r: any) => ({
        date: format(new Date(r.logged_at), 'd MMM', { locale: ru }),
        w: Number(r.weight_kg),
        raw: r.logged_at,
      })),
    );
    setMeasurements(
      (measurementsRes.data ?? []).map((r: any) => ({
        date: format(new Date(r.measured_at), 'd MMM', { locale: ru }),
        rawDate: r.measured_at,
        bf: r.body_fat_percent !== null ? Number(r.body_fat_percent) : null,
        waist: r.waist_cm !== null ? Number(r.waist_cm) : null,
        neck: r.neck_cm !== null ? Number(r.neck_cm) : null,
        hips: r.hips_cm !== null ? Number(r.hips_cm) : null,
        glutes: r.glutes_cm !== null ? Number(r.glutes_cm) : null,
      })),
    );
    setLoading(false);
  }
  if (loading) {
    return (
      <div className="px-5 pt-6 max-w-md mx-auto">
        <Loader />
      </div>
    );
  }
  return (
    <div className="px-5 pt-6 pb-28 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold mb-1">Статистика</h1>
          <p className="text-ink-muted text-sm">Анализ твоего прогресса</p>
        </div>
        <Mascot pose="calm" compact className="w-16 h-16 shrink-0" />
      </div>

      {/* ПОРЯДОК ЭКРАНА ПЕРЕВЁРНУТ 05.09.2026.
          Было: сверху практики (медитации, дыхание, курс), а еда и вес —
          седьмым и восьмым блоком, ниже сгиба. То есть первое, что человек
          видел на статистике, — три нуля: дыханий 0 у всех, медитаций 4
          за 30 дней, курс прошёл 1 человек. А то, ради чего он сюда пришёл
          (1076 приёмов и вес у 21 человека), было спрятано вниз.
          Экран отчитывался не о том, что человек делал, а о том, чего не делал.
          Теперь сверху то, чем пользуются. */}

      {/* Слава — над всеми графиками. Здесь больше всего данных
          во всём приложении, и здесь он молчал: калории лежали отдельно,
          вес отдельно, а связать их человек должен был сам. */}
      <StatsSlavaLine
        days={data}
        weight={weight}
        goalProtein={profile?.daily_protein_g ?? 0}
        rangeDays={range}
      />

      {/* Тело — главный вопрос, ради которого открывают статистику */}
      <BodyChart weight={weight} measurements={measurements} />
      {measurements.length === 0 && (
        <div className="card border-dashed border-line p-4 mt-4 mb-5 text-center">
          <Ruler size={24} className="text-ink-dim mx-auto mb-2" />
          <p className="text-sm text-ink-muted leading-relaxed">
            Внеси замеры тела в Профиле — здесь появятся графики динамики % жира (по формуле U.S. Navy) и обхвата талии.
          </p>
        </div>
      )}

      {/* Сетка активности — дофаминовый якорь, работает у всех */}
      <ConsistencyGrid />

      {/* Range tabs */}
      <div className="card-elevated p-1 grid grid-cols-3 mb-5">
        {([7, 30, 90] as Range[]).map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={`py-2 rounded-lg text-sm transition-all ${
              range === r ? 'bg-bg text-ink' : 'text-ink-muted'
            }`}
          >
            {r} дней
          </button>
        ))}
      </div>
      {/* Calories chart */}
      <div className="card p-4 mb-4">
        <div className="label mb-3">Калории по дням</div>
        <div className="h-44">
          <ResponsiveContainer>
            <LineChart data={data}>
              <CartesianGrid stroke="#222B33" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) =>
                  format(new Date(v), 'd MMM', { locale: ru }).replace('.', '')
                }
                stroke="#5A6573"
                fontSize={11}
              />
              <YAxis stroke="#5A6573" fontSize={11} />
              <Tooltip
                contentStyle={{
                  background: '#141A1F',
                  border: '1px solid #222B33',
                  borderRadius: 12,
                }}
                labelFormatter={(v) =>
                  format(new Date(v), 'd MMMM', { locale: ru })
                }
              />
              {profile?.daily_calories && (
                <ReferenceLine
                  y={profile.daily_calories}
                  stroke="#3DD8FF"
                  strokeDasharray="4 4"
                  label={{ value: 'Цель', fill: '#3DD8FF', fontSize: 10 }}
                />
              )}
              <Line
                type="monotone"
                dataKey="calories"
                stroke="#10E58E"
                strokeWidth={2}
                dot={{ fill: '#10E58E', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Энергобаланс — сразу после калорий: он объясняет связь еды и расхода */}
      <EnergyBalanceCard />

      {/* Практики и курс — ВНИЗ и только если ими пользовались.
          Ноль медитаций лучше не показывать вовсе, чем показывать нулём:
          пустая статистика обесценивает, а не мотивирует. */}
      {(practice.med > 0 || practice.breath > 0 || practice.courseDays > 0) && (
        <div className="mt-6">
          <div className="text-xs text-ink-dim mb-2">Практики</div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {practice.med > 0 && (
              <div className="card p-3 text-center">
                <Emoji e="🧘" className="w-6 h-6 mx-auto mb-1" />
                <div className="text-2xl font-display font-bold text-emerald-glow leading-none">{practice.med}</div>
                <div className="text-[11px] text-ink-dim mt-1">медитаций</div>
              </div>
            )}
            {practice.breath > 0 && (
              <div className="card p-3 text-center">
                <Emoji e="🫁" className="w-6 h-6 mx-auto mb-1" />
                <div className="text-2xl font-display font-bold text-sky-glow leading-none">{practice.breath}</div>
                <div className="text-[11px] text-ink-dim mt-1">дыханий</div>
              </div>
            )}
            {practice.courseDays > 0 && (
              <div className="card p-3 text-center">
                <Emoji e="🧭" className="w-6 h-6 mx-auto mb-1" />
                <div className="text-2xl font-display font-bold text-warn leading-none">{practice.courseDays}</div>
                <div className="text-[11px] text-ink-dim mt-1">дней курса</div>
              </div>
            )}
          </div>
          {practice.weekCount > 0 && <MentalResetBar weekCount={practice.weekCount} />}
        </div>
      )}

      {/* Сон и настроение за месяц — постоянный вход, не только через отметку дня */}
      <Link to="/trend" className="card p-3.5 mt-4 flex items-center gap-3 active:scale-[0.99] transition-transform">
        <div className="w-10 h-10 rounded-xl bg-emerald-glow/15 flex items-center justify-center shrink-0">
          <TrendingUp size={17} className="text-emerald-glow" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">Мой месяц: сон и настроение</div>
          <div className="text-[11px] text-ink-dim">Куда идёт линия и от чего она зависит</div>
        </div>
        <ChevronRight size={16} className="text-ink-dim shrink-0" />
      </Link>

      {/* Пройденный курс живёт здесь, а не на главной */}
      <CourseDoneCard />
    </div>
  );
}
